import type { Collider, World } from "@dimforge/rapier3d-compat";
import type * as THREE from "three";
import { MAX_BLOCKS, type Blocks } from "../blocks";
import { STEP, type Rapier } from "../physics/world";
import type { CameraRig } from "../render/camera";
import { loadDebugOn, saveDebugOn } from "../storage";
import { el } from "./dom";

interface DebugDeps {
  RAPIER: Rapier;
  world: World;
  baseCollider: Collider;
  blocks: Blocks;
  renderer: THREE.WebGLRenderer;
  rig: CameraRig;
  /** The game's own cells: Tower, Tool, Input and Gesture, as [name, text]. */
  status: () => [string, string][];
}

/* =====================================================================
   DEBUG PANEL
   Timings are measured each frame and averaged over 250 ms windows so the
   numbers are readable; the graph shows every frame's time.
   ===================================================================== */
export function initDebug({
  RAPIER,
  world,
  baseCollider,
  blocks,
  renderer,
  rig,
  status,
}: DebugDeps) {
  const dbgEl = el("debug");
  const dbgGrid = el("dbgGrid");
  const spark = el<HTMLCanvasElement>("dbgSpark");
  const sctx = spark.getContext("2d")!;
  const frameHist = new Float32Array(120);
  let histI = 0;
  const newAcc = () => ({
    frames: 0,
    time: 0,
    cpu: 0,
    phys: 0,
    render: 0,
    steps: 0,
    maxFrame: 0,
  });
  let acc = newAcc();
  let debugOn = loadDebugOn();

  // [name, explanation shown on hover, spans two columns]
  const DBG_FIELDS: [string, string, boolean?][] = [
    ["FPS", "Frames drawn per second. Green at 55 and up, red below 30."],
    ["Frame avg", "Average time between frames. 16.7 ms is 60 fps, 33.3 ms is 30 fps."],
    ["Frame max", "The longest frame in the last 250 ms. A spike here is a visible stutter."],
    ["CPU", "Time the game's code takes per frame, physics and render included."],
    ["Physics", "Time per frame spent stepping the physics simulation."],
    [
      "Render",
      "Time per frame three.js takes to hand the scene to the GPU, not the GPU's own time.",
    ],
    [
      "Steps/frame",
      `Physics steps per frame. The simulation moves in fixed 1/${Math.round(1 / STEP)} s steps, so a slow frame runs more of them to catch up.`,
    ],
    ["Engine", "The physics engine and its version."],
    ["Blocks", "Blocks in the world, out of the most allowed."],
    ["Awake", "Blocks still being simulated. Blocks at rest fall asleep and cost almost nothing."],
    ["Contacts", "Pairs of shapes touching or close to it: block on block, or block on the base."],
    ["Draw calls", "Separate draw commands sent to the GPU last frame. Fewer is cheaper."],
    ["Triangles", "Triangles drawn last frame."],
    [
      "Geometries",
      "Geometry buffers three.js holds on the GPU. A count that only grows is a leak.",
    ],
    ["Textures", "Textures three.js holds on the GPU."],
    ["JS memory", "JavaScript memory in use. Only Chromium-based browsers report it."],
    ["Tower", "Height of the settled tower, and the best height so far."],
    [
      "Camera",
      "θ: angle around the tower. φ: tilt from straight down. dist: distance to the focus.",
      true,
    ],
    ["Focus", "The point on the ground the camera looks at, as x, z in metres."],
    ["Tool", "The active tool, plus the drop height when placing.", true],
    ["Input", "The last input used, mouse or touch. Touch shows the action button."],
    [
      "Gesture",
      "What the current press is doing: pending until it moves, then orbit, pan, pinch, grab or ghost (moving the preview).",
    ],
    ["Canvas", "Size of the drawing buffer in device pixels, and the pixel ratio.", true],
  ];
  const cells: Record<string, HTMLElement> = {};
  for (const [name, tip, wide] of DBG_FIELDS) {
    const d = document.createElement("div");
    d.className = "dbg-cell" + (wide ? " wide" : "");
    d.dataset.tip = tip;
    d.innerHTML = `<span>${name}</span><b>–</b>`;
    dbgGrid.appendChild(d);
    cells[name] = d.querySelector("b")!;
  }
  function setCell(name: string, text: string, cls = "") {
    cells[name].textContent = text;
    cells[name].className = cls;
  }

  function setDebug(on: boolean) {
    debugOn = on;
    dbgEl.classList.toggle("hidden", !on);
    acc = newAcc();
    saveDebugOn(on);
  }
  el("vDebug").addEventListener("click", () => setDebug(!debugOn));
  el("dbgClose").addEventListener("click", () => setDebug(false));
  setDebug(debugOn);

  function drawSpark() {
    const w = spark.width,
      h = spark.height,
      k = h / 50,
      n = frameHist.length;
    sctx.clearRect(0, 0, w, h);
    sctx.lineWidth = 2;
    sctx.strokeStyle = "rgba(128, 128, 128, 0.45)"; // 60 fps and 30 fps lines
    for (const t of [16.7, 33.3]) {
      sctx.beginPath();
      sctx.moveTo(0, h - t * k);
      sctx.lineTo(w, h - t * k);
      sctx.stroke();
    }
    sctx.strokeStyle = "#d99a1e";
    sctx.lineWidth = 3;
    sctx.beginPath();
    for (let i = 0; i < n; i++) {
      const v = frameHist[(histI + i) % n];
      const x = (i / (n - 1)) * w,
        y = h - Math.min(v, 50) * k;
      if (i) sctx.lineTo(x, y);
      else sctx.moveTo(x, y);
    }
    sctx.stroke();
  }

  function frame(rawDt: number, physMs: number, renderMs: number, cpuMs: number, steps: number) {
    if (!debugOn) return;
    const ms = rawDt * 1000;
    frameHist[histI] = ms;
    histI = (histI + 1) % frameHist.length;
    acc.frames++;
    acc.time += rawDt;
    acc.cpu += cpuMs;
    acc.phys += physMs;
    acc.render += renderMs;
    acc.steps += steps;
    acc.maxFrame = Math.max(acc.maxFrame, ms);
    drawSpark();
    if (acc.time < 0.25) return;

    const f = acc.frames,
      fps = f / acc.time;
    const msCls = (v: number) => (v <= 17.5 ? "good" : v <= 34 ? "warn" : "bad");
    setCell("FPS", fps.toFixed(0), fps >= 55 ? "good" : fps >= 30 ? "warn" : "bad");
    setCell("Frame avg", ((acc.time * 1000) / f).toFixed(1) + " ms", msCls((acc.time * 1000) / f));
    setCell("Frame max", acc.maxFrame.toFixed(1) + " ms", msCls(acc.maxFrame));
    setCell("CPU", (acc.cpu / f).toFixed(2) + " ms");
    setCell("Physics", (acc.phys / f).toFixed(2) + " ms");
    setCell("Render", (acc.render / f).toFixed(2) + " ms");
    setCell("Steps/frame", (acc.steps / f).toFixed(1));
    setCell("Engine", "Rapier " + RAPIER.version());

    let awake = 0;
    for (const b of blocks.list) if (!b.asleep) awake++;
    setCell("Blocks", `${blocks.list.length} / ${MAX_BLOCKS}`);
    setCell("Awake", `${awake} (${blocks.list.length - awake} asleep)`);
    // Collider pairs whose shapes may be touching (block pairs are seen from both sides)
    let pairs = 0;
    try {
      for (const b of blocks.list)
        world.contactPairsWith(b.collider, (o) => {
          pairs += o.handle === baseCollider.handle ? 1 : 0.5;
        });
      setCell("Contacts", String(Math.round(pairs)));
    } catch {
      setCell("Contacts", "n/a");
    }
    const info = renderer.info;
    setCell("Draw calls", String(info.render.calls));
    setCell("Triangles", info.render.triangles.toLocaleString("en-US"));
    setCell("Geometries", String(info.memory.geometries));
    setCell("Textures", String(info.memory.textures));
    const memory = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
    setCell("JS memory", memory ? (memory.usedJSHeapSize / 1048576).toFixed(0) + " MB" : "n/a");

    const { cam } = rig;
    const deg = (r: number) => ((((r * 180) / Math.PI) % 360) + 360) % 360;
    setCell(
      "Camera",
      `θ ${deg(cam.theta).toFixed(0)}°  φ ${deg(cam.phi).toFixed(0)}°  dist ${rig.viewDistance().toFixed(1)} m`,
    );
    setCell("Focus", `${cam.fx.toFixed(1)}, ${cam.fz.toFixed(1)}`);
    for (const [name, text] of status()) setCell(name, text);
    const c = renderer.domElement;
    setCell("Canvas", `${c.width} × ${c.height} px at ${renderer.getPixelRatio()}×`);
    acc = newAcc();
  }

  return { toggle: () => setDebug(!debugOn), frame };
}
